import React from 'react';
import { ArrowRight, MessageCircle, Sparkles, Zap, Leaf, Recycle } from 'lucide-react';
import { heroStats, partners, contact } from '../mock';

const Hero = ({ onQuote }) => {
  return (
    <section id="home" className="relative overflow-hidden bg-gradient-to-b from-emerald-50/60 via-white to-white">
      {/* decorative grid */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: 'linear-gradient(#10b981 1px, transparent 1px), linear-gradient(90deg, #10b981 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-emerald-200/40 blur-3xl" />
      <div className="absolute top-40 -left-20 w-[400px] h-[400px] rounded-full bg-lime-200/40 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-200 bg-white/70 backdrop-blur text-emerald-700 text-xs font-semibold uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            India's Trusted CBG Energy Partner
          </div>

          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-[#0b1a1a]">
            Clean <span className="text-emerald-600">Green Energy</span> <br />for a <span className="relative inline-block">Brighter Bharat
              <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" fill="none"><path d="M2 6 Q 50 0, 100 5 T 198 4" stroke="#10b981" strokeWidth="3" strokeLinecap="round"/></svg>
            </span>
          </h1>

          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
            Indian Energreen transforms agricultural waste into premium Compressed Biogas, Bio-Diesel & Solar power — fueling a cleaner, greener, and energy-independent India.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <button onClick={onQuote}
                    className="group inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-7 py-3.5 rounded-full font-semibold shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all">
              Explore Solutions
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 bg-white border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-7 py-3.5 rounded-full font-semibold transition-colors">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
            {heroStats.map((s) => (
              <div key={s.label} className="text-left">
                <div className="text-3xl md:text-4xl font-extrabold text-[#0b1a1a]">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* visual */}
        <div className="relative flex items-center justify-center min-h-[480px]">
          {/* outer rings */}
          <div className="absolute w-[420px] h-[420px] rounded-full border-2 border-dashed border-emerald-300/60 animate-[spin_40s_linear_infinite]" />
          <div className="absolute w-[340px] h-[340px] rounded-full border border-emerald-200" />
          <div className="absolute w-[260px] h-[260px] rounded-full bg-gradient-to-br from-emerald-100 to-lime-100" />

          {/* center leaves */}
          <div className="relative z-10 w-44 h-44 rounded-full bg-white shadow-xl grid place-items-center">
            <div className="flex items-end gap-1">
              <Leaf className="w-14 h-14 text-emerald-700 -rotate-12 -mb-2" fill="currentColor" />
              <Leaf className="w-20 h-20 text-emerald-500" fill="currentColor" />
              <Leaf className="w-14 h-14 text-emerald-700 rotate-12 -mb-2" fill="currentColor" />
            </div>
          </div>

          {/* floating cards */}
          <div className="absolute top-8 -left-2 lg:left-0 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 max-w-[220px] border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 grid place-items-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-sm text-[#0b1a1a]">SATAT Aligned</div>
              <div className="text-xs text-slate-500">IOC · BPCL · HPCL</div>
            </div>
          </div>

          <div className="absolute bottom-12 -right-2 lg:right-0 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 max-w-[220px] border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-[#0b1a1a] grid place-items-center">
              <Zap className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="font-bold text-sm text-[#0b1a1a]">Zero Waste</div>
              <div className="text-xs text-slate-500">Clean energy cycle</div>
            </div>
          </div>

          <div className="absolute top-1/2 -right-4 lg:right-2 bg-white rounded-2xl shadow-xl p-3 flex items-center gap-2 border border-slate-100">
            <Recycle className="w-5 h-5 text-emerald-600" />
            <span className="text-xs font-bold text-[#0b1a1a]">100% Renewable</span>
          </div>
        </div>
      </div>

      {/* partners marquee */}
      <div className="relative border-t border-slate-100 bg-white/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-5 overflow-hidden">
          <div className="flex gap-12 animate-[marquee_30s_linear_infinite] whitespace-nowrap">
            {[...partners, ...partners].map((p, i) => (
              <span key={i} className="text-sm font-bold text-slate-400 uppercase tracking-wider">{p}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
